/**
 * <TutorHomeOffer> — l'offre de cours qui attend une réponse, sur l'accueil du masteur.
 *
 * La maquette A1 et le livre V16 (p. 45) posent l'offre sur l'accueil avec « Accepter » : le
 * masteur répond sans aller dans l'onglet. L'accueil d'aujourd'hui ne la montre pas (ses deux
 * cartes, `GroupAttributionProposalsCard` et `SubstitutionProposalsCard`, ne sont plus montées) ;
 * les mots sont donc ceux de l'onglet (`ProposalsTabScreen.js`) : l'intitulé de la boîte de
 * l'offre (« Nouvelle proposition de groupe », « … pour toi », « Nouvelle séance d'essai »,
 * « Nouvelle proposition de remplacement »), le titre et les créneaux de sa carte
 * (`<TutorOfferLineText>`, celle de l'onglet), la première séance.
 *
 * Deux gestes, deux au plus : « Accepter » ouvre la confirmation de l'offre (« Je candidate »,
 * `<TutorOfferFlow>` à son étape `confirm`) ; « Voir » ouvre l'offre entière (son étape `detail`,
 * où « Masquer » vit). Une seule offre montrée, la plus récente en attente ; au-delà, « Toutes les
 * offres » ouvre l'onglet.
 *
 * Un changement de créneau : sans intitulé, son titre le nomme déjà ; « Accepter » seul, qui ouvre le
 * choix de ses horaires, où « Je ne peux pas » vit aussi — `<TutorOfferFlow>` n'a pas d'autre étape
 * pour lui, « Voir » ouvrirait la même.
 *
 * Sans liseré : sur l'accueil, l'offre est seule de son espèce, et son intitulé la nomme ; le liseré
 * de l'onglet distingue une offre en attente de ses voisines déjà répondues (`<TutorOfferCard>`), ce
 * qu'il n'y a pas à faire ici.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { pushButtonBoxHeight } from "../../atoms/Buttons/Button/Button"
import { Card } from "../../molecules/Card"
import { List } from "../../molecules/List"
import { tutorOfferAudienceBadge } from "../../organisms/TutorOfferCard"
import { TutorOfferLineText } from "../../organisms/TutorOfferDetails"
import { tutorOfferTitle } from "../../templates/TutorOfferFlow"
import { type UITutorOffer } from "../../../ports/primary/ui/types/tutorDispoOffres"

const TITLE = "Offre de cours"

export type TutorHomeOfferProps = {
    offer: UITutorOffer
    /** Les offres en attente, celle-ci comprise : au-delà d'une, « Toutes les offres ». */
    pendingCount?: number
    onAccept?: (offer: UITutorOffer) => void
    onOpen?: (offer: UITutorOffer) => void
    /** « Toutes les offres » : l'onglet Offres de cours. */
    onOpenAll?: () => void
    testID?: string
}

export function TutorHomeOffer(props: TutorHomeOfferProps) {
    const { offer, pendingCount = 1, onAccept, onOpen, onOpenAll, testID } = props
    const startLabel = offer.detail.startLabel
    const audience = tutorOfferAudienceBadge(offer)

    return (
        <List
            title={TITLE}
            headerRight={
                pendingCount > 1 && onOpenAll != null ? (
                    <TextButton label={`Toutes les offres (${pendingCount})`} onPress={onOpenAll} size="sm" />
                ) : undefined
            }
            testID={testID}
        >
            <Card p="4">
                <Stack gap="3">
                    <Stack gap="1">
                        {/* Un changement de créneau se nomme dans son titre (« Changement de créneau : Léa (4ème) »). */}
                        {offer.slotChange == null && (
                            <Text size="text-xs" weight="bold" color="text-muted" uppercase>
                                {tutorOfferTitle(offer)}
                            </Text>
                        )}
                        <Text size="text-lg" weight="bold" color="text-primary">
                            {offer.title}
                        </Text>
                        {offer.lines.map((line) => (
                            <TutorOfferLineText key={line.text} line={line} muted size="text-sm" />
                        ))}
                        {startLabel != null && startLabel !== "" && (
                            <Text size="text-sm" color="text-muted">
                                {startLabel}
                            </Text>
                        )}
                    </Stack>
                    {(audience != null || offer.temporary === true) && (
                        <Row gap="1" style={styles.wrap}>
                            {audience != null && <Badge text={audience} variant="info" tinted truncate={false} />}
                            {offer.temporary === true && <Badge text="Temporaire" variant="warning" truncate={false} />}
                        </Row>
                    )}
                    <Row gap="2" style={styles.wrap}>
                        {onAccept != null && <Button label="Accepter" onPress={() => onAccept(offer)} />}
                        {onOpen != null && offer.slotChange == null && (
                            <Button label="Voir" variant="outline" onPress={() => onOpen(offer)} />
                        )}
                    </Row>
                </Stack>
            </Card>
        </List>
    )
}

/** Sa forme dans le squelette de l'accueil : une offre, ses deux boutons. */
export function TutorHomeOfferSkeleton() {
    return (
        <List title={TITLE}>
            <Card p="4">
                <Stack gap="3">
                    <Stack gap="1">
                        <Skeleton textSize="text-xs" width={180} />
                        <Skeleton textSize="text-lg" width={200} />
                        <Skeleton textSize="text-sm" width="80%" />
                    </Stack>
                    <Row gap="2">
                        <Skeleton shape="block" width={104} height={pushButtonBoxHeight()} />
                        <Skeleton shape="block" width={72} height={pushButtonBoxHeight()} />
                    </Row>
                </Stack>
            </Card>
        </List>
    )
}

const styles = StyleSheet.create(() => ({
    wrap: { flexWrap: "wrap" },
}))
